import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Pencil, X, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../api";
import { toast } from "sonner";
import { FileUploader, GalleryUploader } from "../components/FileUploader";
import { PagesPanel } from "./admin/PagesPanel";

const TABS = [
  { id: "stories", label: "Stories" },
  { id: "evidence", label: "Evidence" },
  { id: "bad-ideas", label: "Bad Ideas" },
  { id: "crew", label: "Crew" },
  { id: "products", label: "Products" },
  { id: "pages", label: "Pages" },
  { id: "settings", label: "Site Settings" },
  { id: "users", label: "Users", adminOnly: true },
  { id: "newsletter", label: "Newsletter", adminOnly: true },
  { id: "contact-submissions", label: "Complaints", adminOnly: true },
  { id: "orders", label: "Orders", adminOnly: true },
];

const BLANK_FORMS = {
  stories: { title: "", subtitle: "", cover_image: "", author: "", tags: "", excerpt: "", body: "", published: true },
  evidence: { caption: "", media_type: "image", media_url: "", thumbnail: "", location: "", span: "small" },
  "bad-ideas": { title: "", tagline: "", cover_image: "", risk_level: "MEDIUM", body: "", featured: false },
  crew: {
    name: "", alias: "", role: "", hometown: "", crimes: "", bounty: "", bio: "",
    photo: "", photos: [], videos: [],
    featured_product_slugs: [], featured_deck_slugs: [], related_article_slugs: [],
    instagram: "", twitter: "", youtube: "", tiktok: "", website: "",
    extra_sections: "",
    status: "crew", sort_order: 0, featured: false,
  },
  products: {
    name: "", tagline: "", description: "", price: 0, category: "decks",
    image: "", images: [], story: "", video_url: "", artist_crew_slug: "",
    related_product_slugs: [], in_stock: true, featured: false,
  },
};

const ARRAY_FIELDS = { stories: "tags", crew: "crimes" };
const ARRAY_OF_STRING_FIELDS = {
  products: ["related_product_slugs"],
  crew: ["featured_product_slugs", "featured_deck_slugs", "related_article_slugs"],
};

// Fields that should render as a FileUploader image picker
const IMAGE_FIELDS = new Set(["cover_image", "photo", "image", "thumbnail"]);
// Fields that should render as a video picker
const VIDEO_FIELDS = new Set(["video_url"]);
// Fields that should render as a multi-image gallery picker
const GALLERY_FIELDS = new Set(["images", "photos"]);
// Fields that should render as a multi-video gallery picker
const VIDEO_GALLERY_FIELDS = new Set(["videos"]);
// Long-text fields
const TEXTAREA_FIELDS = new Set(["body", "description", "bio", "story", "extra_sections"]);

// Convert array fields to comma-separated strings when loading into form
function toFormShape(tab, item) {
  const blank = BLANK_FORMS[tab];
  const merged = { ...blank };
  for (const key of Object.keys(blank)) {
    if (item && key in item) {
      const v = item[key];
      if (Array.isArray(v)) merged[key] = v.join(", ");
      else if (v == null) merged[key] = blank[key];
      else merged[key] = v;
    }
  }
  return merged;
}

// Prepare form payload before POST/PUT
function toApiShape(tab, form) {
  const payload = { ...form };
  const arrField = ARRAY_FIELDS[tab];
  if (arrField) {
    payload[arrField] =
      typeof payload[arrField] === "string"
        ? payload[arrField].split(",").map((s) => s.trim()).filter(Boolean)
        : payload[arrField];
  }
  if (tab === "products") payload.price = parseFloat(payload.price);
  if (tab === "crew") payload.sort_order = parseInt(payload.sort_order, 10) || 0;
  return payload;
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("stories");
  const [data, setData] = useState([]);
  const [form, setForm] = useState(BLANK_FORMS.stories);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const isContent = ["stories", "evidence", "bad-ideas", "crew", "products"].includes(tab);
  const isEditable = isContent;
  const isSettings = tab === "settings";
  const isUsers = tab === "users";
  const isPages = tab === "pages";
  const isAdmin = user && user.role === "admin";
  const isEditor = user && (user.role === "admin" || user.role === "moderator");

  const load = async () => {
    if (["settings", "users", "pages"].includes(tab)) {
      setData([]);
      return;
    }
    try {
      const endpoint = ["newsletter", "contact-submissions", "orders"].includes(tab)
        ? `/admin/${tab}`
        : `/${tab}`;
      const { data } = await api.get(endpoint);
      setData(data);
    } catch {
      setData([]);
    }
  };

  /* eslint-disable */
  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "moderator")) {
      setForm(BLANK_FORMS[tab] || {});
      setEditingId(null);
      load();
    }
  }, [tab, user]);
  /* eslint-enable */

  if (loading) return <div className="py-24 text-center font-mono text-xs uppercase tracking-[0.3em]">Checking the rolls…</div>;
  if (!user || !isEditor) return <Navigate to="/admin/login" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm(toFormShape(tab, item));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(BLANK_FORMS[tab]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = toApiShape(tab, form);
      if (editingId) {
        await api.put(`/admin/${tab}/${editingId}`, payload);
        toast.success("Updated.");
      } else {
        await api.post(`/admin/${tab}`, payload);
        toast.success("Saved.");
      }
      setForm(BLANK_FORMS[tab]);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this?")) return;
    try {
      await api.delete(`/admin/${tab}/${id}`);
      toast.success("Gone.");
      if (editingId === id) cancelEdit();
      load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <header className="bg-asphalt text-bone py-6 border-b-4 border-terra">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Boss Door</div>
            <div className="font-display text-3xl uppercase tracking-tight">
              {user.role === "admin" ? "Admin" : "Moderator"} · {user.email}
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border-2 border-bone px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-terra hover:border-terra"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6 border-b-2 border-asphalt">
        <div className="flex gap-2 flex-wrap">
          {TABS.filter((t) => !t.adminOnly || isAdmin).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] border-2 border-asphalt ${
                tab === t.id ? "bg-asphalt text-bone" : "bg-bone hover:bg-asphalt hover:text-bone"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10 grid lg:grid-cols-12 gap-8">
        {isEditable && (
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-5 border-2 border-asphalt bg-card-light shadow-hard p-6 space-y-4 self-start"
            data-testid={editingId ? "admin-edit-form" : "admin-create-form"}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {editingId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                <div className="font-display text-2xl uppercase tracking-tight">
                  {editingId ? "Edit" : "Add new"} {tab.replace("-", " ")}
                </div>
              </div>
              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  data-testid="admin-cancel-edit"
                  className="inline-flex items-center gap-1 px-2 py-1 border-2 border-asphalt font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-asphalt hover:text-bone"
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
              )}
            </div>
            {Object.entries(form).map(([key, val]) => {
              if (typeof val === "boolean") {
                return (
                  <label key={key} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]">
                    <input
                      type="checkbox"
                      checked={val}
                      onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                      data-testid={`admin-field-${key}`}
                    />
                    {key.replace(/_/g, " ")}
                  </label>
                );
              }
              if (Array.isArray(val) && GALLERY_FIELDS.has(key)) {
                return (
                  <GalleryUploader
                    key={key}
                    label={key.replace(/_/g, " ")}
                    value={val}
                    onChange={(arr) => setForm({ ...form, [key]: arr })}
                    testid={`admin-field-${key}`}
                    accept="image"
                  />
                );
              }
              if (Array.isArray(val) && VIDEO_GALLERY_FIELDS.has(key)) {
                return (
                  <GalleryUploader
                    key={key}
                    label={key.replace(/_/g, " ")}
                    value={val}
                    onChange={(arr) => setForm({ ...form, [key]: arr })}
                    testid={`admin-field-${key}`}
                    accept="video"
                  />
                );
              }
              if (IMAGE_FIELDS.has(key)) {
                return (
                  <FileUploader
                    key={key}
                    label={key.replace(/_/g, " ")}
                    accept="image"
                    value={val}
                    onChange={(url) => setForm({ ...form, [key]: url })}
                    testid={`admin-field-${key}`}
                  />
                );
              }
              if (VIDEO_FIELDS.has(key)) {
                return (
                  <FileUploader
                    key={key}
                    label={key.replace(/_/g, " ")}
                    accept="video"
                    value={val}
                    onChange={(url) => setForm({ ...form, [key]: url })}
                    testid={`admin-field-${key}`}
                  />
                );
              }
              if (key === "media_url" && form.media_type === "image") {
                return (
                  <FileUploader
                    key={key}
                    label="media (image)"
                    accept="image"
                    value={val}
                    onChange={(url) => setForm({ ...form, [key]: url })}
                    testid={`admin-field-${key}`}
                  />
                );
              }
              if (key === "media_url" && form.media_type === "video") {
                return (
                  <FileUploader
                    key={key}
                    label="media (video)"
                    accept="video"
                    value={val}
                    onChange={(url) => setForm({ ...form, [key]: url })}
                    testid={`admin-field-${key}`}
                  />
                );
              }
              if (Array.isArray(val) && (ARRAY_OF_STRING_FIELDS[tab] || []).includes(key)) {
                return (
                  <div key={key}>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">
                      {key.replace(/_/g, " ")} (comma separated slugs)
                    </label>
                    <input
                      type="text"
                      value={val.join(", ")}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                        })
                      }
                      data-testid={`admin-field-${key}`}
                      className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
                    />
                  </div>
                );
              }
              if (key === "category") {
                return (
                  <div key={key}>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">category</label>
                    <select
                      value={val}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`admin-field-${key}`}
                      className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
                    >
                      <option value="decks">decks</option>
                      <option value="apparel">apparel</option>
                      <option value="accessories">accessories</option>
                      <option value="limited-runs">limited-runs</option>
                    </select>
                  </div>
                );
              }
              if (key === "status" && tab === "crew") {
                return (
                  <div key={key}>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">
                      status (which section)
                    </label>
                    <select
                      value={val}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`admin-field-${key}`}
                      className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
                    >
                      <option value="crew">Crew</option>
                      <option value="prospects">Prospects</option>
                      <option value="hang-arounds">Hang Arounds</option>
                    </select>
                  </div>
                );
              }
              if (key === "body" || key === "description" || key === "bio" || key === "story" || key === "extra_sections") {
                return (
                  <div key={key}>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">{key.replace(/_/g, " ")}</label>
                    <textarea
                      rows={key === "extra_sections" ? 8 : 5}
                      value={val ?? ""}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`admin-field-${key}`}
                      className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body resize-none"
                      placeholder={
                        key === "extra_sections"
                          ? "Optional. Use --- on its own line to start a new block. The first line of each block becomes its heading."
                          : ""
                      }
                    />
                  </div>
                );
              }
              if (key === "media_type") {
                return (
                  <div key={key}>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">{key.replace(/_/g, " ")}</label>
                    <select
                      value={val}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`admin-field-${key}`}
                      className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
                    >
                      <option value="image">image</option>
                      <option value="video">video</option>
                      <option value="youtube">youtube (video id)</option>
                      <option value="instagram">instagram (post URL)</option>
                    </select>
                    {form.media_type === "instagram" && (
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink mt-1">
                        ⌖ Paste the full IG post URL (e.g. https://www.instagram.com/p/XXXXX/)
                      </p>
                    )}
                    {form.media_type === "youtube" && (
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink mt-1">
                        ⌖ Just the YouTube video id (e.g. ScMzIvxBSi4)
                      </p>
                    )}
                  </div>
                );
              }
              if (key === "span") {
                return (
                  <div key={key}>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">{key}</label>
                    <select
                      value={val}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`admin-field-${key}`}
                      className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
                    >
                      <option value="small">small</option>
                      <option value="wide">wide (2 cols)</option>
                      <option value="tall">tall (2 rows)</option>
                      <option value="large">large (2x2)</option>
                    </select>
                  </div>
                );
              }
              if (key === "risk_level") {
                return (
                  <div key={key}>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">risk level</label>
                    <select
                      value={val}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`admin-field-${key}`}
                      className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
                    >
                      <option>LOW</option>
                      <option>MEDIUM</option>
                      <option>HIGH</option>
                      <option>CATASTROPHIC</option>
                    </select>
                  </div>
                );
              }
              return (
                <div key={key}>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">{key.replace(/_/g, " ")}</label>
                  <input
                    type={key === "price" || key === "sort_order" ? "number" : "text"}
                    step={key === "price" ? "0.01" : undefined}
                    value={val ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    data-testid={`admin-field-${key}`}
                    className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
                  />
                </div>
              );
            })}
            <button
              type="submit"
              disabled={submitting}
              data-testid={editingId ? "admin-edit-submit" : "admin-create-submit"}
              className="w-full bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
            >
              {submitting ? (editingId ? "Updating…" : "Saving…") : editingId ? "Save Changes" : "Save"}
            </button>
          </form>
        )}

        <div className={isEditable ? "lg:col-span-7" : "lg:col-span-12"}>
          {isSettings ? (
            <SettingsPanel />
          ) : isUsers ? (
            <UsersPanel currentUserId={user.id} />
          ) : isPages ? (
            <PagesPanel />
          ) : (
            <>
              <div className="font-display text-2xl uppercase tracking-tight mb-4">Existing — {data.length}</div>
              <div className="border-2 border-asphalt bg-bone divide-y-2 divide-asphalt max-h-[800px] overflow-auto shadow-hard" data-testid="admin-list">
                {data.length === 0 && <div className="p-4 font-mono text-xs uppercase tracking-[0.25em]">Nothing here.</div>}
                {data.map((d) => (
                  <div key={d.id || d.email || d.session_id} className="flex items-center justify-between gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-lg uppercase tracking-tight truncate">
                        {d.title || d.name || d.caption || d.email || d.session_id}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink truncate">
                        {d.slug || d.category || d.payment_status || d.subscribed_at || d.created_at || d.media_type}
                      </div>
                    </div>
                    {isEditable && (
                      <>
                        <button
                          onClick={() => startEdit(d)}
                          data-testid={`admin-edit-${d.id}`}
                          className="p-2 border-2 border-asphalt hover:bg-asphalt hover:text-bone"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(d.id)}
                          data-testid={`admin-delete-${d.id}`}
                          className="p-2 border-2 border-asphalt hover:bg-terra hover:text-bone"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const SettingsPanel = () => {
  return (
    <div className="space-y-10">
      <SiteHeaderLogoForm />
      <ShopifyConnectForm />
      <ShopFeaturedVideoForm />
    </div>
  );
};

const SiteHeaderLogoForm = () => {
  const [logo, setLogo] = useState("");
  const [saved, setSaved] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api
      .get("/settings/site_header_logo")
      .then((r) => {
        const v = (r.data?.value || "").trim();
        setLogo(v);
        setSaved(v);
      })
      .catch(() => {});
  }, []);

  const dirty = logo.trim() !== saved.trim();

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const value = logo.trim();
      await api.put("/admin/settings/site_header_logo", { value });
      const verify = await api.get("/settings/site_header_logo");
      const persisted = (verify.data?.value || "").trim();
      const ok = persisted === value;
      setSaved(value);
      setStatus({
        kind: "ok",
        message: ok
          ? value
            ? "Saved & verified — refresh any page to see the new logo"
            : "Logo reset — refresh any page to see the default Chibah mark"
          : "Save returned 200 but server still shows old value. Retry, or contact support.",
        at: new Date(),
      });
      toast.success("Site logo saved.");
    } catch (err) {
      const httpStatus = err?.response?.status;
      const detail = formatError(err?.response?.data?.detail);
      const message =
        httpStatus === 401 || httpStatus === 403
          ? "Session expired — log out, log back in, then save again."
          : detail
          ? `Save failed (HTTP ${httpStatus || "?"}): ${detail}`
          : `Save failed: ${err?.message || "Network error"}.`;
      setStatus({ kind: "err", message, at: new Date() });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="border-2 border-asphalt bg-card-light shadow-hard p-6 space-y-5 max-w-3xl"
      data-testid="admin-site-logo-form"
    >
      <div className="flex items-center gap-2">
        <SettingsIcon className="w-5 h-5" />
        <div className="font-display text-2xl uppercase tracking-tight">Site Header Logo</div>
      </div>
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink leading-relaxed">
        The Chibah mark in the top-left of every page. Replace anytime — uploaded PNG/SVG/WebP
        transparency is preserved. Leave blank to fall back to the built-in default. To override
        the logo on a single page only, use the per-page logo field in <b>Pages</b>.
      </p>
      <FileUploader
        accept="image"
        label="Logo image"
        value={logo}
        onChange={(url) => setLogo(url || "")}
        testid="site-logo-uploader"
      />
      <div className="flex flex-wrap gap-3 items-start">
        <button
          type="submit"
          disabled={loading || !dirty}
          data-testid="site-logo-save"
          className="bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
        >
          {loading ? "Saving…" : dirty ? "Save logo" : "All saved"}
        </button>
        {saved && (
          <button
            type="button"
            onClick={() => setLogo("")}
            data-testid="site-logo-clear"
            className="bg-bone text-asphalt border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-asphalt hover:text-bone"
          >
            Use default
          </button>
        )}
        {status && (
          <div
            className={`flex-1 min-w-[220px] border-2 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] leading-snug ${
              status.kind === "ok"
                ? "border-asphalt bg-bone text-asphalt"
                : "border-terra bg-terra/10 text-terra"
            }`}
            data-testid="site-logo-status"
          >
            <div className="text-[10px] opacity-70 mb-0.5">
              {status.at.toLocaleTimeString()} — {status.kind === "ok" ? "✓ saved" : "✗ failed"}
            </div>
            <div className="normal-case tracking-normal text-xs">{status.message}</div>
          </div>
        )}
      </div>
    </form>
  );
};

const ShopifyConnectForm = () => {
  const [domain, setDomain] = useState("");
  const [token, setToken] = useState("");
  const [ignoreInventory, setIgnoreInventory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [test, setTest] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const refreshTest = async () => {
    try {
      const r = await api.get("/admin/shop/test");
      setTest(r.data);
    } catch (e) {
      setTest({ ok: false, reason: "request_failed", message: formatError(e?.response?.data?.detail) });
    }
  };

  const refreshProducts = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/shop/cache/clear");
      const r = await api.get("/shop/products?limit=200");
      const items = r.data?.items || [];
      const inStock = items.filter((p) => p.in_stock).length;
      await refreshTest();
      toast.success(`Re-pulled ${items.length} products from Shopify (${inStock} in stock).`);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Refresh failed — check connection.");
    } finally {
      setRefreshing(false);
    }
  };

  const runDiagnostic = async () => {
    setDiagnosing(true);
    try {
      const r = await api.get("/admin/shop/diagnostic");
      setDiagnostic(r.data);
    } catch (e) {
      setDiagnostic({ ok: false, message: formatError(e?.response?.data?.detail) || "Diagnostic failed." });
    } finally {
      setDiagnosing(false);
    }
  };

  /* eslint-disable */
  useEffect(() => {
    api.get("/settings/shopify_store_domain").then((r) => setDomain(r.data?.value || "")).catch(() => {});
    api.get("/settings/shopify_storefront_access_token").then((r) => setToken(r.data?.value || "")).catch(() => {});
    api.get("/settings/shop_ignore_inventory").then((r) => setIgnoreInventory(!!r.data?.value)).catch(() => {});
    refreshTest();
  }, []);

  // Plain event handler; not an effect. Lint plugin (react-hooks v5) misflags it.
  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/admin/settings/shopify_store_domain", { value: (domain || "").trim().toLowerCase() });
      await api.put("/admin/settings/shopify_storefront_access_token", { value: (token || "").trim() });
      await api.put("/admin/settings/shop_ignore_inventory", { value: !!ignoreInventory });
      await api.post("/admin/shop/cache/clear");
      toast.success("Shopify settings saved. Cache cleared.");
      await refreshTest();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setLoading(false);
    }
  };
  /* eslint-enable */

  return (
    <form
      onSubmit={save}
      className="border-4 border-terra bg-card-light shadow-hard p-6 space-y-5 max-w-3xl"
      data-testid="admin-shopify-form"
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <SettingsIcon className="w-5 h-5 text-terra" />
        <div className="font-display text-2xl uppercase tracking-tight">Shopify Storefront</div>
        {test?.ok && (
          <span
            className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] bg-asphalt text-bone px-2 py-1"
            data-testid="shopify-connected-badge"
          >
            ✦ Connected · {test.mode === "public-json" ? "Public Feed" : "Storefront API"}
            {typeof test.product_count === "number" && (
              <> · {test.product_count} product{test.product_count === 1 ? "" : "s"}</>
            )}
            {typeof test.collection_count === "number" && (
              <> · {test.collection_count} collection{test.collection_count === 1 ? "" : "s"}</>
            )}
          </span>
        )}
        {test && !test.ok && (
          <span
            className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] bg-terra text-bone px-2 py-1"
            data-testid="shopify-disconnected-badge"
          >
            ✦ {test.reason === "missing_credentials" ? "Not Connected" : "Connection Error"}
          </span>
        )}
      </div>
      {test?.ok && test.token_error && (
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-terra bg-terra/10 p-3" data-testid="shopify-token-warning">
          ✦ Storefront API token rejected — falling back to public feed. Error: {test.token_error}
        </div>
      )}
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink">
        Paste your <code>.myshopify.com</code> domain to start displaying products immediately
        — <b>token is optional</b>.
        Add a Storefront API token <b>later</b> if you want metafield-driven editorial extras (story / artist / video).
      </p>
      <details className="border-2 border-dashed border-asphalt bg-bone p-3" data-testid="shopify-help-token">
        <summary className="font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer">
          ⚙︎ (Optional) How do I get a Storefront API access token? · 2026 flow
        </summary>
        <p className="font-body text-sm mt-3 text-ink">
          ⚠️ This must be done in a <b>desktop browser</b> at <code>admin.shopify.com</code>. The Shopify mobile app does not expose the custom-app workflow.
        </p>
        <ol className="font-body text-sm mt-2 space-y-1 list-decimal list-inside text-ink">
          <li>Open <b>admin.shopify.com</b> on desktop and sign in.</li>
          <li>Click <b>Settings</b> (bottom-left gear icon).</li>
          <li>Click <b>Apps and sales channels</b> → top-right click <b>Develop apps</b>.</li>
          <li>If asked, click <b>Allow custom app development</b> and confirm twice.</li>
          <li>Click <b>Create an app</b> → name it <code>Chibah Storefront</code> → <b>Create app</b>.</li>
          <li>In the new app, click <b>Configuration</b> tab → find <b>Storefront API integration</b> → click <b>Configure</b>.</li>
          <li>Tick these scopes:
            <code className="block font-mono text-xs mt-1 ml-4 leading-relaxed">
              unauthenticated_read_product_listings<br/>
              unauthenticated_read_product_tags<br/>
              unauthenticated_read_product_inventory<br/>
              unauthenticated_write_checkouts<br/>
              unauthenticated_read_checkouts<br/>
              unauthenticated_read_metafields (only if using PDP extras)
            </code>
          </li>
          <li>Click <b>Save</b>. Back at the app overview, click <b>Install app</b> (top right) → <b>Install</b>.</li>
          <li>Click the <b>API credentials</b> tab → scroll to <b>Storefront API access token</b> → click <b>Reveal token once</b> → copy the value (starts with <code>shpat_</code>).</li>
          <li>Come back here, paste it below, and hit <b>Save & test</b>.</li>
        </ol>
      </details>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">
          Store domain <span className="text-terra">(required)</span>
        </label>
        <input
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="chibah.myshopify.com"
          data-testid="shopify-domain"
          className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-mono"
        />
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink mt-1">
          Find this in Shopify → Settings → Domains (Shopify-managed domain).
        </div>
      </div>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">
          Storefront API access token <span className="text-ink/60">(optional — unlocks editorial PDP extras)</span>
        </label>
        <input
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          type="password"
          data-testid="shopify-token"
          className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-mono"
        />
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink mt-1">
          Stored server-side. Never exposed to the browser. Leave blank to use Shopify&apos;s public product feed.
        </div>
      </div>

      {/* Inventory override — for on-demand fulfillment (BoardPusher etc.) */}
      <label
        className="block border-2 border-asphalt bg-bone p-3 cursor-pointer hover:bg-card-light"
        data-testid="shopify-ignore-inventory-row"
      >
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={ignoreInventory}
            onChange={(e) => setIgnoreInventory(e.target.checked)}
            data-testid="shopify-ignore-inventory"
            className="mt-1 w-4 h-4 accent-terra"
          />
          <div>
            <div className="font-display text-base uppercase tracking-tight">
              This store doesn&apos;t track inventory
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink mt-1 leading-relaxed">
              Forces every product &amp; variant to render as IN STOCK regardless of what Shopify reports. Use this for on-demand fulfillment (BoardPusher, print-on-demand) where Shopify&apos;s SOLD OUT flag is meaningless. Hides the SOLD OUT badge and re-enables the Add to Bag button.
            </div>
          </div>
        </div>
      </label>
      {test && !test.ok && test.message && (
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] border-2 border-terra bg-terra/10 p-3" data-testid="shopify-test-error">
          ✦ {test.message}
        </div>
      )}
      <div className="flex gap-3 flex-wrap">
        <button
          type="submit"
          disabled={loading}
          data-testid="shopify-save"
          className="bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
        >
          {loading ? "Saving…" : "Save & test connection"}
        </button>
        <button
          type="button"
          onClick={refreshProducts}
          disabled={refreshing}
          data-testid="shopify-cache-clear"
          className="bg-bone text-asphalt border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-asphalt hover:text-bone disabled:opacity-50"
        >
          {refreshing ? "Refreshing…" : "Refresh products"}
        </button>
      </div>

      {/* Diagnostic — shows what Shopify is actually reporting for availability */}
      <div className="border-t-2 border-asphalt/30 pt-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={runDiagnostic}
            disabled={diagnosing}
            data-testid="shopify-diagnostic"
            className="bg-bone text-asphalt border-2 border-asphalt px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone disabled:opacity-50"
          >
            {diagnosing ? "Checking…" : "Run availability diagnostic"}
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
            Helps debug &quot;sold out&quot; when inventory looks fine
          </span>
        </div>
        {diagnostic && (
          <div className="border-2 border-asphalt bg-bone p-4 font-mono text-[11px] leading-relaxed space-y-1" data-testid="shopify-diagnostic-result">
            <div className="font-display text-base uppercase mb-2 border-b-2 border-asphalt/30 pb-1">Connection report</div>
            <div>Mode: <b>{diagnostic.mode || "—"}</b></div>
            <div>Domain: <b>{diagnostic.domain || "(none)"}</b></div>
            <div>Token: <b>{diagnostic.token_set ? (diagnostic.token_mask || "set") : "(none — using public feed)"}</b></div>
            {diagnostic.shop_ping && (
              <div>
                Token check:{" "}
                {diagnostic.shop_ping.ok ? (
                  <b className="text-asphalt">PASS → connected to &quot;{diagnostic.shop_ping.name}&quot;</b>
                ) : (
                  <b className="text-terra">FAIL — {diagnostic.shop_ping.message}</b>
                )}
              </div>
            )}
            {typeof diagnostic.raw_count === "number" && (
              <div>Products returned by Shopify: <b>{diagnostic.raw_count}</b></div>
            )}
            {typeof diagnostic.filtered_count === "number" && diagnostic.filtered_count !== diagnostic.raw_count && (
              <div>After our filters: <b>{diagnostic.filtered_count}</b></div>
            )}
            {typeof diagnostic.in_stock_count === "number" && (
              <div>In stock: <b>{diagnostic.in_stock_count}</b> &middot; Sold out: <b>{diagnostic.out_of_stock_count}</b></div>
            )}
            {diagnostic.sample && (
              <div className="mt-2 pt-2 border-t-2 border-asphalt/30">
                <div className="font-display text-base uppercase mb-1">Sample product</div>
                <div>{diagnostic.sample.title} <span className="opacity-60">({diagnostic.sample.handle})</span></div>
                <div>availableForSale: <b className={diagnostic.sample.available_for_sale ? "text-asphalt" : "text-terra"}>{String(diagnostic.sample.available_for_sale)}</b></div>
                <div>Variants available: <b>{diagnostic.sample.variants_available}/{diagnostic.sample.variants_total}</b></div>
                <div>Price: <b>${diagnostic.sample.price ?? "—"}</b> &middot; Image: <b>{diagnostic.sample.image ? "yes" : "no"}</b></div>
                <div>Tags: <b>{(diagnostic.sample.tags || []).join(", ") || "(none)"}</b></div>
              </div>
            )}
            {diagnostic.errors && diagnostic.errors.length > 0 && (
              <div className="mt-2 pt-2 border-t-2 border-terra/40">
                <div className="font-display text-base uppercase mb-1 text-terra">Errors</div>
                {diagnostic.errors.map((er) => (
                  <div key={er} className="text-terra">{er}</div>
                ))}
              </div>
            )}
            {diagnostic.hint && (
              <div className="mt-3 border-l-4 border-terra pl-3 bg-card-light p-2">
                <div className="font-display text-base uppercase mb-1">Likely cause &amp; fix</div>
                <div className="whitespace-pre-line">{diagnostic.hint}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

const ShopFeaturedVideoForm = () => {
  const [video, setVideo] = useState({ url: "", poster: "", title: "", subtitle: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/settings/shop_featured_video")
      .then((r) => {
        const v = r.data?.value || {};
        setVideo({ url: v.url || "", poster: v.poster || "", title: v.title || "", subtitle: v.subtitle || "" });
      })
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/admin/settings/shop_featured_video", { value: video });
      toast.success("Featured video saved. It's live on /shop.");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={save} className="border-2 border-asphalt bg-card-light shadow-hard p-6 space-y-5 max-w-3xl" data-testid="admin-settings-form">
      <div className="flex items-center gap-2 mb-2">
        <SettingsIcon className="w-5 h-5" />
        <div className="font-display text-2xl uppercase tracking-tight">Featured Shop Video</div>
      </div>
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink">
        Upload the clip you want playing at the top of <b>Spend Some Money</b>. Replace anytime.
      </p>
      <FileUploader
        accept="video"
        label="Video file"
        value={video.url}
        onChange={(url) => setVideo({ ...video, url })}
        testid="settings-video-file"
      />
      <FileUploader
        accept="image"
        label="Poster / thumbnail"
        value={video.poster}
        onChange={(url) => setVideo({ ...video, poster: url })}
        testid="settings-video-poster"
      />
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Headline (optional)</label>
        <input
          value={video.title}
          onChange={(e) => setVideo({ ...video, title: e.target.value })}
          className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
          data-testid="settings-video-title"
        />
      </div>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Caption (optional)</label>
        <input
          value={video.subtitle}
          onChange={(e) => setVideo({ ...video, subtitle: e.target.value })}
          className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
          data-testid="settings-video-subtitle"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="settings-video-save"
        className="bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
      >
        {loading ? "Saving…" : "Save featured video"}
      </button>
    </form>
  );
};

const UsersPanel = ({ currentUserId }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "moderator" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      setUsers([]);
    }
  };

  /* eslint-disable */
  useEffect(() => {
    load();
  }, []);

  // Plain event handler; not an effect. Lint plugin (react-hooks v5) misflags it.
  const createUser = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Email and password required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User added.");
      setForm({ email: "", password: "", name: "", role: "moderator" });
      load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not create user.");
    } finally {
      setBusy(false);
    }
  };
  /* eslint-enable */

  const removeUser = async (id, email) => {
    if (!window.confirm(`Remove ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User removed.");
      load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not remove user.");
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-users-panel">
      <form
        onSubmit={createUser}
        className="lg:col-span-5 border-2 border-asphalt bg-card-light shadow-hard p-6 space-y-4 self-start"
        data-testid="admin-users-form"
      >
        <div className="flex items-center gap-2 mb-2">
          <Plus className="w-4 h-4" />
          <div className="font-display text-2xl uppercase tracking-tight">Add User</div>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
          Moderators can edit content + upload media. Admins also see orders, newsletter, complaints, and this panel.
        </p>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="user-form-email"
            className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Display name (optional)</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="user-form-name"
            className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Password (min 6 chars)</label>
          <input
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="user-form-password"
            className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.25em] mb-1">Role</label>
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="user-form-role"
            className="w-full border-2 border-asphalt bg-bone px-3 py-2 font-body"
          >
            <option value="moderator">Moderator (content only)</option>
            <option value="admin">Admin (full access)</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="user-form-submit"
          className="w-full bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
        >
          {busy ? "Saving…" : "Add User"}
        </button>
      </form>

      <div className="lg:col-span-7">
        <div className="font-display text-2xl uppercase tracking-tight mb-4">Users — {users.length}</div>
        <div className="border-2 border-asphalt bg-bone divide-y-2 divide-asphalt shadow-hard" data-testid="admin-users-list">
          {users.length === 0 && (
            <div className="p-4 font-mono text-xs uppercase tracking-[0.25em]">Just you, partner.</div>
          )}
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-3 p-3" data-testid={`admin-user-row-${u.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg uppercase tracking-tight truncate">{u.email}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink truncate">
                  {u.name || "—"} · {u.role}
                </div>
              </div>
              <div className={`font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 border-2 ${
                u.role === "admin" ? "border-terra text-terra" : "border-asphalt text-asphalt"
              }`}>
                {u.role}
              </div>
              {u.id !== currentUserId ? (
                <button
                  onClick={() => removeUser(u.id, u.email)}
                  data-testid={`admin-user-delete-${u.id}`}
                  className="p-2 border-2 border-asphalt hover:bg-terra hover:text-bone"
                  title="Remove user"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              ) : (
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">that&apos;s you</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
